import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, Check } from "lucide-react";
import { STYLES, STYLE_BY_ID, useStyleStore } from "@/store/styleStore";

/** Swatch preview built from a style's own tokens. */
function Swatch({ id }: { id: string }) {
  const s = STYLE_BY_ID[id];
  return (
    <span
      className="flex h-6 w-9 shrink-0 overflow-hidden rounded-md ring-1 ring-black/20"
      style={{ backgroundColor: `rgb(${s.tokens.bg})` }}
    >
      <span className="m-auto flex gap-1">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: `rgb(${s.tokens.primary})` }} />
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: `rgb(${s.tokens.accent})` }} />
      </span>
    </span>
  );
}

export default function StyleSwitcher() {
  const { styleId, setStyle } = useStyleStore();
  const [open, setOpen] = useState(false);
  const current = STYLE_BY_ID[styleId];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-2 text-xs font-semibold text-muted transition-colors hover:bg-surface-2 hover:text-text"
        aria-label="Change visual style"
      >
        <Palette className="h-[18px] w-[18px]" />
        <span className="hidden font-display tracking-wide sm:inline">{current.name}</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 z-40 mt-2 w-72 overflow-hidden rounded-xl border border-border bg-surface p-1.5 shadow-glow"
            >
              <div className="px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
                Visual style
              </div>
              {STYLES.map((s) => {
                const active = s.id === styleId;
                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      setStyle(s.id);
                      setOpen(false);
                    }}
                    className={[
                      "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
                      active ? "bg-surface-2" : "hover:bg-surface-2",
                    ].join(" ")}
                  >
                    <Swatch id={s.id} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="font-display text-sm font-semibold tracking-wide text-text">
                          {s.name}
                        </span>
                        {active && <Check className="h-3.5 w-3.5 text-primary" />}
                      </span>
                      <span className="block truncate text-xs text-muted">{s.tagline}</span>
                    </span>
                  </button>
                );
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
